// The three count histograms, drawn as SVG.
//
// NO CHARTING LIBRARY. Three bar charts over at most a few dozen categories is
// what a `<rect>` per bar does, and a library would be a dependency in the
// bundle for arithmetic this file does in ten lines. D9 closed charting as
// in-app rather than moving it to Fabric on the same reasoning: the job is
// trivial and keeping it here avoids a workspace, per-user licensing and a
// second authentication surface.
//
// LABELS SHOW PERCENT, NOT COUNTS, which is the source's behaviour. The API
// sends counts and the total beside them, deliberately -- a percentage computed
// server-side is a number a client cannot re-scale when it filters, and
// rounding it there loses the count. So the division happens here, and the
// count is in the bar's tooltip rather than lost.

export interface Bar {
  label: string;
  count: number;
}

export interface HistogramProps {
  bars: Bar[];
  total: number;
  /** `blue` is the source's default; `red` and `green` are what it sets on the
   *  commodity and job charts. */
  colour?: 'blue' | 'red' | 'green';
  orientation?: 'vertical' | 'horizontal';
  /** The source's *Show legend*, which is on for the action-category and
   *  commodity charts and off for the job chart.
   *
   *  A LEGEND IS NOT THE AXIS LABELS. Widening the room under the bars so the
   *  rotated category labels stopped being clipped fixed a different complaint;
   *  the second sign-off still reported no legend, correctly. This renders one:
   *  a keyed list of the categories under the chart, with each one's share. */
  legend?: boolean;
  height?: number;
  /** A category count long enough to be unreadable, cut to the top n with the
   *  rest summed. The job chart is the one that needs it: every open barrier's
   *  order is a bar. */
  top?: number;
}

const COLOURS = { blue: '#1e88e5', red: '#e53935', green: '#43a047' };

export function Histogram({
  bars,
  total,
  colour = 'blue',
  orientation = 'vertical',
  legend = true,
  height = 320,
  top,
}: HistogramProps) {
  const ordered = [...bars].sort((a, b) => b.count - a.count);
  const shown = top && ordered.length > top ? ordered.slice(0, top) : ordered;
  const remainder = top && ordered.length > top ? ordered.slice(top).reduce((sum, b) => sum + b.count, 0) : 0;
  const data = remainder > 0 ? [...shown, { label: `${ordered.length - top!} others`, count: remainder }] : shown;

  const max = Math.max(1, ...data.map((b) => b.count));
  const fill = COLOURS[colour];
  const percent = (count: number) => (total > 0 ? `${Math.round((count / total) * 100)}%` : '0%');

  if (data.length === 0) return <p className="muted">Nothing to chart.</p>;

  const key = legend ? (
    <ul className="histogram-legend">
      {data.map((bar) => (
        <li key={bar.label}>
          <span className="swatch" style={{ background: fill }} aria-hidden="true" />
          {bar.label}
          <span className="muted"> {percent(bar.count)}</span>
        </li>
      ))}
    </ul>
  ) : null;

  if (orientation === 'horizontal') {
    const rowHeight = 26;
    return (
      <>
      <svg
        className="histogram"
        role="img"
        viewBox={`0 0 600 ${data.length * rowHeight + 8}`}
        style={{ width: '100%', height: data.length * rowHeight + 8 }}
      >
        {data.map((bar, index) => {
          const width = (bar.count / max) * 380;
          return (
            <g key={bar.label} transform={`translate(0, ${index * rowHeight})`}>
              <title>{`${bar.label}: ${bar.count} of ${total}`}</title>
              <text x={0} y={17} className="histogram-label">
                {truncate(bar.label, 26)}
              </text>
              <rect x={190} y={5} width={width} height={16} fill={fill} rx={2} />
              <text x={196 + width} y={17} className="histogram-value">
                {percent(bar.count)}
              </text>
            </g>
          );
        })}
      </svg>
      {key}
      </>
    );
  }

  const columnWidth = Math.max(40, Math.min(90, 600 / data.length));
  // The category labels need real room. They were drawn into 56px before, which
  // rotated them off the bottom of the viewBox and presented as a chart with no
  // legend at all -- which is how the sign-off reported it.
  const labelRoom = legend ? 104 : 28;
  const plot = height - labelRoom;
  return (
    <>
    <svg
      className="histogram"
      role="img"
      viewBox={`0 0 ${Math.max(600, data.length * columnWidth)} ${height}`}
      style={{ width: '100%', height }}
    >
      {data.map((bar, index) => {
        const barHeight = (bar.count / max) * plot;
        const x = index * columnWidth;
        return (
          <g key={bar.label}>
            <title>{`${bar.label}: ${bar.count} of ${total}`}</title>
            <rect x={x + 6} y={plot - barHeight + 20} width={columnWidth - 12} height={barHeight} fill={fill} rx={2} />
            <text x={x + columnWidth / 2} y={plot - barHeight + 14} className="histogram-value" textAnchor="middle">
              {percent(bar.count)}
            </text>
            {legend && (
              <text
                x={x + columnWidth / 2}
                y={plot + 34}
                className="histogram-label"
                textAnchor="end"
                transform={`rotate(-35, ${x + columnWidth / 2}, ${plot + 34})`}
              >
                {truncate(bar.label, 22)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
    {key}
    </>
  );
}

function truncate(text: string, length: number): string {
  return text.length <= length ? text : `${text.slice(0, length - 1)}…`;
}
